import React, { useState } from 'react';
import Hero from '../../components/Hero/Hero';
import Pathfinder from '../../components/Pathfinder/Pathfinder';
import Metadata from '../../components/Metadata/Metadata';
import { Box, Typography } from '@mui/material';
import Accordion from '../../components/Accordion/Accordion';
import Section from '../../components/Section/Section';
import Wallpaper from '../../components/Wallpaper/Wallpaper';
import Card from '../../components/Card/Card';
import GlobalCallout from '../../components/Global/CalloutDefault';
import Button from '../../components/Button/Button';
import Image from '../../components/Image/Image';
import ContentSplit from '../../components/ContentSplit/ContentSplit';
import ListItem from '../../components/ListItem/ListItem';
import { Link } from 'react-router-dom';

const PageContent: React.FC = () => {
  const [expandAll, setExpandAll] = useState(false);

  const toggleAll = () => {
    setExpandAll(!expandAll);
  };

  return (
    <Box>
      <Metadata
        title="Industries Served | General Genomics Data Solutions"
        description="Explore the key sectors General Genomics serves, from healthcare to national security. Discover innovative and secure data solutions tailored to meet the unique challenges of each industry."
      />
      <Hero
        helper="Industries"
        headline="Advanced analytics tailored for government and commercial industries"
      />

      <Section theme="" className="cards-large-icons">
        <h2 className="action-on-view action-fade-in">
          Transform Your Data Solutions
        </h2>
        <p className="action-on-view action-fade-in">
          Unlock the full potential of your data with our innovative solutions
          tailored to your industry&apos;s unique challenges. Whether
          you&apos;re in government, healthcare, or commercial enterprises, we
          provide cutting-edge tools like predictive analytics, bioinformatics,
          and workforce optimization. Contact us today to see how our secure,
          scalable, and user-friendly platform can enhance decision-making and
          operational efficiency. Experience the impact of personalized data
          insights for your organization.
        </p>
        <Image
          path="/images/library/military-mechanic-aircraft-repair-2.webp"
          size="wide"
          className="action-on-view action-fade-in"
          alt="This is a wide image."
        />
      </Section>

      <ContentSplit
        image="/images/library/military-mechanic-men-repair.webp"
        position="right"
      >
        <p className="helper action-on-view action-fade-in">Explore Benefits</p>
        <h2 className="action-on-view action-fade-in">Why partner with us?</h2>
        <ListItem
          icon="check"
          iconClass="highlighted-icon"
          className="custom-list-item"
        >
          <p>
            <strong>Tailored Solutions:</strong> Discover how our platform
            addresses your specific industry challenges with innovative data
            strategies.
          </p>
        </ListItem>
        <ListItem
          icon="check"
          iconClass="highlighted-icon"
          className="custom-list-item"
        >
          <p>
            <strong>Seamless Integration:</strong> See firsthand how easily our
            tools integrate into your existing workflows and enhance efficiency.
          </p>
        </ListItem>
        <ListItem
          icon="check"
          iconClass="highlighted-icon"
          className="custom-list-item"
        >
          <p>
            <strong>Actionable Insights:</strong> Explore predictive analytics
            and data visualizations that empower confident decision-making.
          </p>
        </ListItem>
        <ListItem
          icon="check"
          iconClass="highlighted-icon"
          className="custom-list-item"
        >
          <p>
            <strong>Proven Expertise:</strong> Benefit from decades of
            experience in healthcare, national security, pharmaceuticals, and
            more.
          </p>
        </ListItem>
        <ListItem
          icon="check"
          iconClass="highlighted-icon"
          className="custom-list-item"
        >
          <p>
            <strong>Scalable and Secure:</strong> Understand our commitment to
            data security and scalability for organizations of all sizes.
          </p>
        </ListItem>
      </ContentSplit>

      <Section theme="dark" className="section-cross-linking">
        <h2 className="action-on-view action-fade-in">
          Explore More of Our Solutions
        </h2>
        <p className="action-on-view action-fade-in">
          Discover additional resources to learn about our public sector
          solutions, commercial offerings, datasets, and technologies powering
          General Genomics.
        </p>
        <div className="card-group">
          <Card
            icon="assured_workload"
            headline="Government Solutions"
            subhead="Explore our solutions designed for the public sector, driving innovation and efficiency."
            btnURL="/industries/government"
            btnLabel="Learn more"
            className="action-on-view action-fade-in"
          />
          <Card
            icon="business"
            headline="Commercial Solutions"
            subhead="Unlock powerful data solutions tailored for businesses and research organizations."
            btnURL="/industries/commercial"
            btnLabel="Learn more"
            className="action-on-view action-fade-in"
          />
          <Card
            icon="folder_open"
            headline="Data Portal"
            subhead="Access a wide range of datasets and contact us to see how they can drive your work."
            btnURL="/data-portal"
            btnLabel="Learn more"
            className="action-on-view action-fade-in"
          />
          <Card
            icon="settings"
            headline="Our Capabilities"
            subhead="Dive into the cutting-edge technologies that power our genomics solutions."
            btnURL="/our-approach/our-capabilities"
            btnLabel="Learn more"
            className="action-on-view action-fade-in"
          />
        </div>
      </Section>

      <Wallpaper asset="../images/library/lab-technician-man-laptop-5.webp"></Wallpaper>

      <Section>
        <Typography variant="h2" className="action-on-view action-fade-in">
          Frequently asked questions about General Genomics&apos; industry
          solutions
        </Typography>
        <Typography className="action-on-view action-fade-in">
          Learn how General Genomics supports government and commercial sectors
          with secure, innovative data solutions for diverse applications.
        </Typography>
        <Box className="accordion-wrapper action-on-view action-fade-in">
          <Button
            className="accordion-button"
            type="outlined"
            onClick={toggleAll}
            icon={expandAll ? 'expand_less' : 'expand_more'}
            label={expandAll ? 'Collapse All' : 'Expand All'}
          />
          <Accordion
            isOpen={expandAll}
            headline="What industries does General Genomics serve?"
          >
            General Genomics provides secure and innovative data solutions
            across a variety of sectors, including healthcare, public health,{' '}
            <Link to="/industries/government/national-security">
              national security,
            </Link>{' '}
            <Link to="/industries/commercial/pharmaceuticals">
              pharmaceuticals,
            </Link>{' '}
            and more.
          </Accordion>
          <Accordion
            isOpen={expandAll}
            headline="How can General Genomics support my organization’s data needs?"
          >
            We offer tailored solutions in bioinformatics, predictive analytics,
            and workforce optimization to help both{' '}
            <Link to="/industries/government">government</Link> and{' '}
            <Link to="/industries/commercial">commercial</Link> organizations
            address unique challenges.
          </Accordion>
          <Accordion
            isOpen={expandAll}
            headline="What specific services does General Genomics provide for government sectors?"
          >
            Our government solutions focus on data security and optimization,
            with applications in public health,{' '}
            <Link to="/industries/government/national-security">
              national security,
            </Link>{' '}
            and other critical public services.
          </Accordion>
          <Accordion
            isOpen={expandAll}
            headline="How does General Genomics assist commercial businesses?"
          >
            We offer data solutions for businesses and research organizations,
            driving innovation in industries such as{' '}
            <Link to="/industries/commercial/pharmaceuticals">
              pharmaceuticals
            </Link>{' '}
            and healthcare.
          </Accordion>
        </Box>
      </Section>

      <Section theme="dark" bgName="lines" className="section-call-to-action">
        <Typography variant="h2" className="action-on-view action-fade-in">
          Why partnering with General Genomics transforms bioinformatics
          research and innovation
        </Typography>
        <Box className="card-group">
          <Card
            icon="check"
            headline="Advanced Solutions in Analytics"
            subhead="General Genomics combines analytics and innovation with cutting-edge algorithms, large language models, machine learning, and predictive analytics to transform synthetic systems and complex data insights."
            className="action-on-view action-fade-in"
          />
          <Card
            icon="check"
            headline="Precision Insights"
            subhead="Harness predictive modeling and genomic data analysis to drive accurate, actionable insights tailored to research and healthcare objectives."
            className="action-on-view action-fade-in action-delay-250"
          />
          <Card
            icon="check"
            headline="Collaborative Expertise"
            subhead="Partner with a team experienced in supporting government initiatives to advance bioinformatics research and public health strategies."
            className="action-on-view action-fade-in action-delay-500"
          />
        </Box>
      </Section>

      <GlobalCallout />

      <Pathfinder />
    </Box>
  );
};

export default PageContent;
